import { useId } from 'react'
import { HtmlContent } from '#components/inputs'
import { Checkbox } from '#primitives/inputs'
import { SignaturePad } from '#primitives/inputs'
import { DownloadIcon, InfoIcon } from '#icons'
import type { WaiverSignaturePaths } from '@lgc/database/types'

/**
 * WaiverConsentCard — the member-facing "read and sign" block.
 *
 * One composition shared by the booking widget and mobile onboarding, because
 * the two are the same block at two widths and a club's legal document should
 * not read differently depending on which app opened it.
 *
 * Fully controlled and behaviour-free: it renders, it reports changes, and it
 * never fetches, navigates, or submits. The parent owns what signing means in
 * its flow — which matters here because the two surfaces submit at different
 * moments (immediately for a signed-in member, alongside account creation for a
 * guest booker).
 *
 * @example
 * ```tsx
 * <WaiverConsentCard
 *   title={waiver.title}
 *   contentHtml={waiver.content}
 *   requireSignature={waiver.requireSignature}
 *   requireAgreement={waiver.requireAgreement}
 *   agreementText={waiver.agreementText}
 *   signature={signature}
 *   onSignatureChange={setSignature}
 *   agreed={agreed}
 *   onAgreedChange={setAgreed}
 * />
 * ```
 */
export interface WaiverConsentCardProps {
  title: string
  /** Sanitized HTML from the club's rich-text editor. */
  contentHtml: string
  requireSignature: boolean
  requireAgreement: boolean
  agreementText: string | null
  signature: WaiverSignaturePaths | null
  onSignatureChange: (value: WaiverSignaturePaths | null) => void
  agreed: boolean
  onAgreedChange: (value: boolean) => void
  /** Omit to hide the download affordance entirely. */
  onDownloadPdf?: () => void
  disabled?: boolean
  /** Height of the signature surface. Lower it on small screens. @default 180 */
  signatureHeight?: number
  /** Max height of the scrollable document body. @default 200 */
  contentMaxHeight?: number
  /**
   * Narrow-screen treatment: the download and clear affordances collapse to
   * circular icon buttons, matching the phone frames.
   */
  compact?: boolean
  /**
   * Render the agreement checkbox inside the card. Turn it off when the surface
   * pins the checkbox somewhere else — the phone design puts it in a sticky
   * footer above the submit button. Pair with `WaiverAgreementCheckbox` so both
   * placements render the same control.
   * @default true
   */
  showAgreement?: boolean
  className?: string
  'data-testid'?: string
}

/**
 * Standard electronic-signature disclosure. Fixed copy rather than a prop: it is
 * a legal statement about what signing here means, and it should not vary per
 * club or per surface.
 */
const SIGNATURE_DISCLOSURE =
  'By signing below, I confirm that I have read and agree to the terms of this waiver. My electronic signature is legally equivalent to a handwritten signature.'


export function WaiverConsentCard({
  title,
  contentHtml,
  requireSignature,
  requireAgreement,
  agreementText,
  signature,
  onSignatureChange,
  agreed,
  onAgreedChange,
  onDownloadPdf,
  disabled = false,
  signatureHeight = 180,
  contentMaxHeight = 200,
  compact = false,
  showAgreement = true,
  className = '',
  'data-testid': dataTestId,
}: WaiverConsentCardProps) {
  return (
    <div className={`space-y-4 ${className}`} data-testid={dataTestId}>
      <div className="rounded-xl border border-butter-gray-200">
        <div className="flex items-center justify-between gap-3 border-b border-butter-gray-200 px-4 py-3">
          <h3 className="text-lg font-semibold text-butter-gray-900">{title}</h3>
          {onDownloadPdf && (
            <button
              type="button"
              onClick={onDownloadPdf}
              // Compact collapses to the circular icon button the phone frames
              // show; the label still reaches assistive tech via aria-label.
              aria-label={compact ? 'Download PDF' : undefined}
              // `theme-*`, not `butter-brand-*`: this card is member-facing and
              // rendered only by mobile-app and web-portal-2, never admin. A
              // fixed brand purple here would ignore the tenant's palette.
              className={
                compact
                  ? 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-butter-gray-50 text-theme-600 text-interactive:bg-butter-gray-100'
                  : 'flex shrink-0 items-center gap-1.5 text-base font-medium text-theme-600 text-interactive:text-theme-700'
              }
              data-testid={dataTestId ? `${dataTestId}-download` : undefined}
            >
              <DownloadIcon className="h-5 w-5" />
              {!compact && 'Download PDF'}
            </button>
          )}
        </div>
        <div className="overflow-y-auto px-4 py-3" style={{ maxHeight: contentMaxHeight }}>
          <HtmlContent html={contentHtml} className="text-sm text-butter-gray-600" />
        </div>
      </div>

      {requireSignature && (
        <div className="space-y-2">
          <SignaturePad
            value={signature}
            onChange={onSignatureChange}
            disabled={disabled}
            height={signatureHeight}
            compact={compact}
            data-testid={dataTestId ? `${dataTestId}-signature` : undefined}
          />
          <p className="flex gap-2 text-xs text-butter-gray-500">
            <InfoIcon className="mt-px h-4 w-4 shrink-0 text-butter-blue-500" />
            <span>{SIGNATURE_DISCLOSURE}</span>
          </p>
        </div>
      )}

      {showAgreement && requireAgreement && agreementText && (
        <WaiverAgreementCheckbox
          agreementText={agreementText}
          agreed={agreed}
          onAgreedChange={onAgreedChange}
          disabled={disabled}
          data-testid={dataTestId ? `${dataTestId}-agree` : undefined}
        />
      )}
    </div>
  )
}

export interface WaiverAgreementCheckboxProps {
  /**
   * Input id the label points at. Defaults to a generated one — pass a value
   * only if something outside needs to target the checkbox.
   *
   * Deriving this from the waiver title (as it once did) collides whenever a
   * club runs two waivers with the same name, at which point both labels toggle
   * the first checkbox.
   */
  id?: string
  agreementText: string
  agreed: boolean
  onAgreedChange: (value: boolean) => void
  disabled?: boolean
  className?: string
  'data-testid'?: string
}

/**
 * The "I have read and agree" control, on its own.
 *
 * Split out so a surface that pins it away from the card — the phone design's
 * sticky footer — renders the identical control rather than a lookalike.
 */
export function WaiverAgreementCheckbox({
  id,
  agreementText,
  agreed,
  onAgreedChange,
  disabled = false,
  className = '',
  'data-testid': dataTestId,
}: WaiverAgreementCheckboxProps) {
  const generatedId = useId()
  const checkboxId = id ?? generatedId

  return (
    <div className={`flex items-start gap-2.5 ${className}`}>
      <Checkbox
        id={checkboxId}
        checked={agreed}
        onCheckedChange={(checked) => onAgreedChange(checked === true)}
        disabled={disabled}
        className="mt-0.5"
        data-testid={dataTestId}
      />
      <label htmlFor={checkboxId} className="text-sm text-butter-gray-700">
        {agreementText}
      </label>
    </div>
  )
}

/**
 * Whether a waiver's requirements are satisfied.
 *
 * Exported alongside the card so callers gate their submit button on the same
 * rule the server enforces, instead of each surface re-deriving it and one of
 * them getting it wrong.
 */
export function isWaiverConsentComplete(input: {
  requireSignature: boolean
  requireAgreement: boolean
  signature: WaiverSignaturePaths | null
  agreed: boolean
}): boolean {
  if (input.requireSignature && (!input.signature || input.signature.strokes.length === 0)) return false
  if (input.requireAgreement && !input.agreed) return false
  return true
}
